/**
 * Hauptkomponente der 3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation Anwendung
 * Verwendet das Cloudscape Design System für eine konsistente und moderne Benutzeroberfläche
 */

import React, { useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  Alert,
  AppLayout,
  Box,
  ContentLayout,
  Header,
  SpaceBetween,
  Tabs,
} from '@cloudscape-design/components';
import '@cloudscape-design/global-styles/index.css';
import { Mode, applyMode } from '@cloudscape-design/global-styles';

// Removed Amplify Authenticator - using API Key authentication instead

import { AppZustand, SimulationOptions, SteuerParameter } from '@/types';
import { DATENSCHUTZ_URL, IMPRESSUM_URL, NAVIGATION_TABS } from '@/utils/constants';
import {
  activateGoogleAnalytics,
  getStoredAnalyticsConsent,
  isAnalyticsEnabled,
  trackPageView,
} from '@/utils/analytics';
import { getDetailedVersionInfo } from '@/utils/version';

import AnalyticsConsentModal from '@/components/AnalyticsConsentModal';
import BuyMeACoffeeButton from '@/components/BuyMeACoffeeButton';
import Schritt0 from '@/pages/Schritt0';
import Schritt1 from '@/pages/Schritt1';
import Schritt2 from '@/pages/Schritt2';
import Schritt3 from '@/pages/Schritt3';
import Schritt4 from '@/pages/Schritt4';

/**
 * Hauptkomponente mit State Management und Navigation
 */
const App: React.FC = () => {
  // Frame-busting protection: Prevent unauthorized iframe embedding
  useEffect(() => {
    // Check if the application is running inside an iframe
    if (window.self !== window.top) {
      try {
        // Check if the parent frame is from an authorized domain
        const parentOrigin = document.referrer;
        const allowedDomains = ['https://www.svenesis.org'];

        // If parent is from an allowed domain, allow embedding
        const isAuthorized = allowedDomains.some(domain => parentOrigin.startsWith(domain));

        if (!isAuthorized) {
          // Attempt to break out of unauthorized iframe
          if (window.top) {
            window.top.location.href = window.self.location.href;
          }
        } else {
          // Log authorized embedding for debugging
          // Authorized iframe embedding detected
        }
      } catch {
        // If cross-origin restrictions prevent referrer check, try alternative method
        try {
          // Use postMessage to communicate with parent and verify origin
          window.parent.postMessage({ type: '3TOPF_PORTFOLIO_ORIGIN_CHECK' }, '*');

          // Listen for response from parent
          const handleOriginResponse = (event: MessageEvent) => {
            const allowedOrigins = ['https://www.svenesis.org'];

            if (allowedOrigins.includes(event.origin)) {
              // Authorized iframe embedding detected
              window.removeEventListener('message', handleOriginResponse);
              return;
            }

            // Unauthorized embedding detected - show warning
            showUnauthorizedEmbeddingWarning();
            window.removeEventListener('message', handleOriginResponse);
          };

          window.addEventListener('message', handleOriginResponse);

          // Fallback: If no response within 1 second, assume unauthorized
          setTimeout(() => {
            window.removeEventListener('message', handleOriginResponse);
            // Iframe embedding from unknown origin detected
            showUnauthorizedEmbeddingWarning();
          }, 1000);
        } catch {
          // Final fallback - show warning for any iframe embedding we can't verify
          // Unable to verify iframe embedding origin
          showUnauthorizedEmbeddingWarning();
        }
      }
    }

    function showUnauthorizedEmbeddingWarning() {
      // Create and show warning overlay
      const warningOverlay = document.createElement('div');
      warningOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 0, 0, 0.9);
        color: white;
        z-index: 999999;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        font-family: Arial, sans-serif;
        font-size: 18px;
        line-height: 1.5;
      `;

      warningOverlay.innerHTML = `
        <div style="max-width: 600px; padding: 20px;">
          <h2 style="color: #fff; margin-bottom: 20px;">🚫 Unauthorized Embedding Detected</h2>
          <p>This application is not authorized to run in an iframe for security reasons.</p>
          <p>Please visit the application directly at its official URL.</p>
          <button onclick="window.open('${window.location.origin}', '_blank')" 
                  style="margin-top: 20px; padding: 10px 20px; font-size: 16px; 
                         background: #fff; color: #000; border: none; border-radius: 4px; 
                         cursor: pointer;">
            Open Application Directly
          </button>
        </div>
      `;

      document.body.appendChild(warningOverlay);
    }
  }, []);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppContent />
    </Router>
  );
};

/**
 * App-Inhalt mit Routing und State Management
 * Getrennt von der Hauptkomponente für bessere Router-Integration
 */
const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookieSettingsSignal, setCookieSettingsSignal] = useState(0);

  useEffect(() => {
    if (!isAnalyticsEnabled()) {
      return;
    }
    if (getStoredAnalyticsConsent() === 'granted') {
      activateGoogleAnalytics();
    }
  }, []);

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  // Zentraler Anwendungszustand
  const [appZustand, setAppZustand] = useState<AppZustand>(() => {
    // Try to load parameters from localStorage on startup
    const savedParameters = localStorage.getItem('portfolio-3topf-parameters');
    const savedSimulationOptions = localStorage.getItem('portfolio-3topf-simulation-options');
    const savedCrisisDefinition = localStorage.getItem('portfolio-3topf-crisis-definition');

    let steuerParameter = null;
    let simulationOptions: SimulationOptions = {
      inflationOption: 'parameter-inflation',
      investmentReturnOption: 'konstante-rendite',
      tagesgeldOption: 'parameter-rendite',
      zinstopfOption: 'parameter-rendite',
      isLogarithmicScale: false,
    };
    let crisisDefinition = 'negative-return';

    if (savedParameters) {
      try {
        steuerParameter = JSON.parse(savedParameters);
      } catch (error) {
        console.warn('Failed to parse saved parameters:', error);
        // Clear invalid data
        localStorage.removeItem('portfolio-3topf-parameters');
      }
    }

    if (savedSimulationOptions) {
      try {
        simulationOptions = { ...simulationOptions, ...JSON.parse(savedSimulationOptions) };
      } catch (error) {
        console.warn('Failed to parse saved simulation options:', error);
        // Clear invalid data
        localStorage.removeItem('portfolio-3topf-simulation-options');
      }
    }

    if (savedCrisisDefinition) {
      try {
        crisisDefinition = JSON.parse(savedCrisisDefinition);
      } catch (error) {
        console.warn('Failed to parse saved crisis definition:', error);
        // Clear invalid data
        localStorage.removeItem('portfolio-3topf-crisis-definition');
      }
    }

    return {
      steuerParameter,
      simulationOptions,
      crisisDefinition,
      simulationState: {
        // Single simulation state
        simulationData: [],
        isSimulating: false,
        simulationError: null,

        // 1-Topf simulation state
        oneTopfSimulationData: [],
        isRunning1TopfSimulation: false,
        oneTopfSimulationError: null,

        // Monte Carlo state
        monteCarloResults: [],
        monteCarloStats: null,
        isRunningMonteCarlo: false,
        monteCarloProgress: 0,
        monteCarloError: null,
      },
    };
  });

  // Navigation State
  const [activeTabId, setActiveTabId] = useState<string>('schritt0');

  // Theme Management
  const [currentTheme, setCurrentTheme] = useState<Mode>(() => {
    // Detect system theme preference
    if (typeof window !== 'undefined') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? Mode.Dark
        : Mode.Light;
      const savedTheme = localStorage.getItem('cloudscape-theme') as Mode;
      return savedTheme || systemTheme;
    }
    return Mode.Light;
  });

  // Apply theme on mount and theme change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      applyMode(currentTheme);
      localStorage.setItem('cloudscape-theme', currentTheme);
    }
  }, [currentTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleThemeChange = (e: MediaQueryListEvent) => {
        const newTheme = e.matches ? Mode.Dark : Mode.Light;
        setCurrentTheme(newTheme);
      };

      mediaQuery.addListener(handleThemeChange);
      return () => mediaQuery.removeListener(handleThemeChange);
    }
  }, []);

  // Effekt für die Synchronisation der aktiven Tab mit der Route
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/schritt0') {
      setActiveTabId('schritt0');
    } else if (currentPath === '/schritt1') {
      setActiveTabId('schritt1');
    } else if (currentPath === '/schritt2') {
      setActiveTabId('schritt2');
    } else if (currentPath === '/schritt3') {
      setActiveTabId('schritt3');
    } else if (currentPath === '/schritt4') {
      setActiveTabId('schritt4');
    }
  }, [location.pathname]);

  /**
   * Handler für Berechnungsparameter-Updates aus Schritt 1
   */
  const handleSteuerParameterUpdate = (parameter: SteuerParameter) => {
    setAppZustand(prev => ({
      ...prev,
      steuerParameter: parameter,
      // Clear simulation results when parameters change
      simulationState: {
        ...prev.simulationState,
        simulationData: [],
        oneTopfSimulationData: [],
        monteCarloResults: [],
        monteCarloStats: null,
        simulationError: null,
        oneTopfSimulationError: null,
        monteCarloError: null,
      },
    }));

    // Persist parameters to localStorage
    try {
      localStorage.setItem('portfolio-3topf-parameters', JSON.stringify(parameter));
    } catch (error) {
      console.warn('Failed to save parameters to localStorage:', error);
    }
  };

  /**
   * Handler für Simulation-Options-Updates (synchronized across all tabs)
   */
  const handleSimulationOptionsUpdate = (options: Partial<SimulationOptions>) => {
    setAppZustand(prev => ({
      ...prev,
      simulationOptions: {
        ...prev.simulationOptions,
        ...options,
      },
      // Clear simulation results when parameters change
      simulationState: {
        ...prev.simulationState,
        simulationData: [],
        oneTopfSimulationData: [],
        monteCarloResults: [],
        monteCarloStats: null,
        simulationError: null,
        oneTopfSimulationError: null,
        monteCarloError: null,
      },
    }));

    // Persist simulation options to localStorage
    try {
      const updatedOptions = { ...appZustand.simulationOptions, ...options };
      localStorage.setItem('portfolio-3topf-simulation-options', JSON.stringify(updatedOptions));
    } catch (error) {
      console.warn('Failed to save simulation options to localStorage:', error);
    }
  };

  const handleCrisisDefinitionUpdate = (crisisDefinition: string) => {
    setAppZustand(prev => ({
      ...prev,
      crisisDefinition,
      // Clear simulation results when crisis definition changes
      simulationState: {
        ...prev.simulationState,
        simulationData: [],
        oneTopfSimulationData: [],
        monteCarloResults: [],
        monteCarloStats: null,
        simulationError: null,
        oneTopfSimulationError: null,
        monteCarloError: null,
      },
    }));

    // Persist crisis definition to localStorage
    try {
      localStorage.setItem('portfolio-3topf-crisis-definition', JSON.stringify(crisisDefinition));
    } catch (error) {
      console.warn('Failed to save crisis definition to localStorage:', error);
    }
  };

  /**
   * Handler für Simulation-State-Updates
   */
  const handleSimulationStateUpdate = (updates: Partial<AppZustand['simulationState']>) => {
    setAppZustand(prev => ({
      ...prev,
      simulationState: {
        ...prev.simulationState,
        ...updates,
      },
    }));
  };

  /**
   * Tab-Change Handler
   */
  const handleTabChange = (detail: { activeTabId: string }) => {
    const tabId = detail.activeTabId;
    setActiveTabId(tabId);

    // Navigation basierend auf Tab-ID
    if (tabId === 'schritt0') {
      navigate('/schritt0');
    } else if (tabId === 'schritt1') {
      navigate('/schritt1');
    } else if (tabId === 'schritt2') {
      navigate('/schritt2');
    } else if (tabId === 'schritt3') {
      navigate('/schritt3');
    } else if (tabId === 'schritt4') {
      navigate('/schritt4');
    }
  };

  // Tab-Konfiguration - alle Tabs sind immer verfügbar
  const tabs = NAVIGATION_TABS.map(tab => ({
    id: tab.id,
    label: tab.label,
    disabled: false,
  }));

  return (
    <>
      <AppLayout
      headerSelector="#top-navigation"
      navigationHide={true}
      toolsHide={true}
      disableContentPaddings={true}
      content={
        <div className="app-page-gutter">
          <div className="app-page-gutter-bmc-slot">
            <BuyMeACoffeeButton />
          </div>
          <ContentLayout
            disableOverlap={true}
            header={
              <div className="app-contentlayout-header-inner">
                <Header variant="h1">
                  <span className="app-header-title-row">
                    <img
                      src="/svenesis-logo.svg"
                      alt="Svenesis"
                      width={40}
                      height={40}
                      style={{ flexShrink: 0, display: 'block' }}
                    />
                    <span className="app-header-title-text">
                      3-Topf vs. 1-Topf Portfolio Monte-Carlo Simulation
                    </span>
                  </span>
                </Header>
              </div>
            }
          >
            <SpaceBetween direction="vertical" size="l">
              {/* Build · Impressum · Datenschutz */}
              <Box textAlign="left" fontSize="body-s">
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <Box variant="span" display="inline" color="text-status-info">
                    {getDetailedVersionInfo()}
                  </Box>
                  <Box variant="span" display="inline" color="text-body-secondary">
                    ·
                  </Box>
                  <Box variant="span" display="inline" color="text-status-info">
                    <a
                      href={IMPRESSUM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-meta-line-link"
                    >
                      Impressum
                    </a>
                  </Box>
                  <Box variant="span" display="inline" color="text-body-secondary">
                    ·
                  </Box>
                  <Box variant="span" display="inline" color="text-status-info">
                    <a
                      href={DATENSCHUTZ_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="app-meta-line-link"
                    >
                      Datenschutz
                    </a>
                  </Box>
                  {isAnalyticsEnabled() && (
                    <>
                      <Box variant="span" display="inline" color="text-body-secondary">
                        ·
                      </Box>
                      <Box variant="span" display="inline" color="text-status-info">
                        <button
                          type="button"
                          className="app-meta-line-link"
                          onClick={() => setCookieSettingsSignal(n => n + 1)}
                        >
                          Cookie-Einstellungen
                        </button>
                      </Box>
                    </>
                  )}
                </SpaceBetween>
              </Box>

              {/* Hinweise über den aktuellen Status */}
              {!appZustand.steuerParameter && (
                <Alert
                  type="info"
                  dismissAriaLabel="Schließen"
                  header="Berechnungsparameter erforderlich"
                >
                  Bitte füllen Sie zunächst die Berechnungsparameter in Schritt 1 aus, um
                  fortzufahren.
                </Alert>
              )}

              {/* Hauptnavigation als Tabs */}
              <Tabs
                activeTabId={activeTabId}
                onChange={({ detail }) => handleTabChange(detail)}
                tabs={tabs}
                ariaLabel="Hauptnavigation"
                variant="default"
              />

              {/* Hauptinhalt basierend auf der Route */}
              <main role="main" aria-label="Hauptinhalt">
                <Routes>
                  <Route path="/schritt0" element={<Schritt0 />} />
                  <Route
                    path="/schritt1"
                    element={
                      <Schritt1
                        steuerParameter={appZustand.steuerParameter}
                        onParameterUpdate={handleSteuerParameterUpdate}
                      />
                    }
                  />
                  <Route
                    path="/schritt2"
                    element={
                      <Schritt2
                        steuerParameter={appZustand.steuerParameter}
                        simulationOptions={appZustand.simulationOptions}
                        crisisDefinition={appZustand.crisisDefinition}
                        onSimulationOptionsUpdate={handleSimulationOptionsUpdate}
                        onCrisisDefinitionUpdate={handleCrisisDefinitionUpdate}
                        simulationState={appZustand.simulationState}
                        onSimulationStateUpdate={handleSimulationStateUpdate}
                      />
                    }
                  />
                  <Route
                    path="/schritt3"
                    element={
                      <Schritt3
                        steuerParameter={appZustand.steuerParameter}
                        simulationOptions={appZustand.simulationOptions}
                        crisisDefinition={appZustand.crisisDefinition}
                        onSimulationOptionsUpdate={handleSimulationOptionsUpdate}
                        onCrisisDefinitionUpdate={handleCrisisDefinitionUpdate}
                        simulationState={appZustand.simulationState}
                        onSimulationStateUpdate={handleSimulationStateUpdate}
                      />
                    }
                  />
                  <Route
                    path="/schritt4"
                    element={
                      <Schritt4
                        steuerParameter={appZustand.steuerParameter}
                        simulationOptions={appZustand.simulationOptions}
                        crisisDefinition={appZustand.crisisDefinition}
                        onSimulationOptionsUpdate={handleSimulationOptionsUpdate}
                        onCrisisDefinitionUpdate={handleCrisisDefinitionUpdate}
                        simulationState={appZustand.simulationState}
                        onSimulationStateUpdate={handleSimulationStateUpdate}
                      />
                    }
                  />
                  <Route path="/" element={<Navigate to="/schritt0" replace />} />
                  <Route path="*" element={<Navigate to="/schritt0" replace />} />
                </Routes>
              </main>
            </SpaceBetween>
          </ContentLayout>
        </div>
      }
      />
      <AnalyticsConsentModal reopenSignal={cookieSettingsSignal} />
    </>
  );
};

export default App;
